interface AnimatedWebpConfig {
  threshold: number;
  rootMargin: string;
  zoneStart: number; // poměr odshora (0–1), výchozí 0.25
  zoneEnd: number; // poměr odshora (0–1), výchozí 0.75
}

interface AnimatedWebpInstance {
  destroy: () => void;
  updateConfig: (newConfig: Partial<AnimatedWebpConfig>) => void;
  updateVisibleElement: () => void;
}

const createAnimatedWebpImage = (
  config: Partial<AnimatedWebpConfig> = {}
): AnimatedWebpInstance => {
  let isMobile: boolean;
  let isLandscape: boolean;
  let currentlyAnimatedElement: HTMLElement | null = null;
  let loopId: number | null = null;

  const defaultConfig: AnimatedWebpConfig = {
    threshold: 1.0,
    rootMargin: '-80px 0px 0px 0px',
    zoneStart: 0.25,
    zoneEnd: 0.75,
    ...config,
  };

  const SELECTORS = {
    animatedWebp: '.global-image__animated-webp',
    staticImage: '.global-image__img--has-animated-webp',
    container: '.global-image__container--has-animated-webp',
  };

  const detectMobile = (): boolean => window.innerWidth <= 679;
  const detectLandscape = (): boolean => window.innerWidth > window.innerHeight;

  const showAnimatedWebp = (
    animatedWebp: HTMLImageElement,
    staticImage: HTMLImageElement,
    container: HTMLElement
  ): void => {
    if (currentlyAnimatedElement && currentlyAnimatedElement !== container) {
      const prevAnimatedWebp = currentlyAnimatedElement.querySelector(
        SELECTORS.animatedWebp
      ) as HTMLImageElement;
      const prevStaticImage = currentlyAnimatedElement.querySelector(
        SELECTORS.staticImage
      ) as HTMLImageElement;

      if (prevAnimatedWebp && prevStaticImage) {
        prevAnimatedWebp.style.opacity = '0';
        prevStaticImage.style.opacity = '1';
      }
    }

    animatedWebp.style.opacity = '1';
    staticImage.style.opacity = '0';
    currentlyAnimatedElement = container;
  };

  const hideAnimatedWebp = (
    animatedWebp: HTMLImageElement,
    staticImage: HTMLImageElement
  ): void => {
    animatedWebp.style.opacity = '0';
    staticImage.style.opacity = '1';
  };

  /**
   * Kontrola, zda je element v centru viewportu v obou dimenzích (vertikální i horizontální)
   * Vertikálně: musí být ve střední zóně (zoneStart - zoneEnd)
   * Horizontálně: priorita plně viditelných elementů zleva, pokud žádný není plně viditelný, pak nejbližší zleva
   */
  const isInCenterZone = (
    rect: DOMRect,
    viewportWidth: number,
    viewportHeight: number,
    menuOffset: number
  ): { isInZone: boolean; distance: number; isFullyVisibleHorizontally: boolean } => {
    // Kontrola vertikální pozice - musí být ve střední zóně
    const verticalUpperBound = viewportHeight * defaultConfig.zoneStart + menuOffset;
    const verticalLowerBound = viewportHeight * defaultConfig.zoneEnd;
    const viewportVerticalMiddle = viewportHeight / 2;
    const containerVerticalMiddle = rect.top + rect.height / 2;

    const isVerticallyVisible = rect.bottom > 0 && rect.top < viewportHeight;
    const isInVerticalZone =
      isVerticallyVisible &&
      containerVerticalMiddle >= verticalUpperBound &&
      containerVerticalMiddle <= verticalLowerBound;
    const verticalDistance = Math.abs(containerVerticalMiddle - viewportVerticalMiddle);

    // Kontrola horizontální pozice
    const isHorizontallyVisible = rect.right > 0 && rect.left < viewportWidth;
    const isFullyVisibleHorizontally = rect.left >= 0 && rect.right <= viewportWidth;

    // Levá pozice elementu (čím blíž k 0, tím vyšší priorita)
    const leftPosition = Math.max(0, rect.left);

    // Element musí být vertikálně ve zóně a horizontálně viditelný
    const isInZone = isInVerticalZone && isHorizontallyVisible;

    // Pro řazení:
    // 1. Priorita: plně viditelné elementy (přidáme velkou penalizaci pro částečně viditelné)
    // 2. Sekundární: levá pozice (čím blíž k 0, tím lepší)
    // 3. Terciární: vertikální vzdálenost
    const visibilityPenalty = isFullyVisibleHorizontally ? 0 : 100000;
    const distance = visibilityPenalty + leftPosition * 10 + verticalDistance;

    return { isInZone, distance, isFullyVisibleHorizontally };
  };

  const updateVisibleElement = (): void => {
    if (!isMobile || isLandscape) return;

    const containers = Array.from(document.querySelectorAll(SELECTORS.container)) as HTMLElement[];

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const menuOffset = 68; // horní menu – přizpůsob podle reality

    let closestContainer: HTMLElement | null = null;
    let closestDistance = Infinity;

    containers.forEach((container) => {
      const rect = container.getBoundingClientRect();
      const { isInZone, distance } = isInCenterZone(
        rect,
        viewportWidth,
        viewportHeight,
        menuOffset
      );

      if (isInZone && distance < closestDistance) {
        closestDistance = distance;
        closestContainer = container;
      }
    });

    containers.forEach((container) => {
      const animatedWebp = container.querySelector(SELECTORS.animatedWebp) as HTMLImageElement;
      const staticImage = container.querySelector(SELECTORS.staticImage) as HTMLImageElement;
      if (!animatedWebp || !staticImage) return;

      if (container === closestContainer) {
        showAnimatedWebp(animatedWebp, staticImage, container);
      } else {
        hideAnimatedWebp(animatedWebp, staticImage);
      }
    });

    currentlyAnimatedElement = closestContainer;
  };

  const initMobileLoop = (): void => {
    const loop = () => {
      updateVisibleElement();
      loopId = requestAnimationFrame(loop);
    };
    loopId = requestAnimationFrame(loop);
  };

  // přidá loaded class na první hover/focus - kvůli css fade in transitions
  const initHoverLoad = (): void => {
    const containers = Array.from(document.querySelectorAll(SELECTORS.container)) as HTMLElement[];

    containers.forEach((container) => {
      const animatedWebp = container.querySelector(SELECTORS.animatedWebp) as HTMLImageElement;
      if (!animatedWebp) return;

      // Parent pro případ spouštění animace na hover celého article nebo video karty
      const articleParent = container.closest(
        '.article-card__container, .video-card'
      ) as HTMLElement | null;

      const handleLoadTrigger = () => {
        if (!animatedWebp.classList.contains('global-image__animated-webp--loaded')) {
          animatedWebp.classList.add('global-image__animated-webp--loaded');

          // Dočasně nastavíme opacity na 0 přes inline styl
          animatedWebp.style.opacity = '0';

          // Pokud je obrázek už načtený, fade-in hned, jinak počkej na load
          if (animatedWebp.complete) {
            requestAnimationFrame(() => {
              animatedWebp.style.opacity = '';
            });
          } else {
            animatedWebp.addEventListener(
              'load',
              () => {
                requestAnimationFrame(() => {
                  animatedWebp.style.opacity = '';
                });
              },
              { once: true }
            );
          }
        }
      };

      container.addEventListener('mouseenter', handleLoadTrigger, { once: true });
      container.addEventListener('focusin', handleLoadTrigger, { once: true });

      // Hover/focus na article-card__container pokud existuje
      if (articleParent) {
        articleParent.addEventListener('mouseenter', handleLoadTrigger, { once: true });
        articleParent.addEventListener('focusin', handleLoadTrigger, { once: true });
      }
    });
  };

  const init = (): void => {
    isMobile = detectMobile();
    isLandscape = detectLandscape();

    initHoverLoad();

    if (!isMobile) return;

    initMobileLoop(); // smyčka pro mobil
    updateVisibleElement(); // okamžitý první check
  };

  const destroy = (): void => {
    if (loopId !== null) {
      cancelAnimationFrame(loopId);
      loopId = null;
    }

    const containers = Array.from(document.querySelectorAll(SELECTORS.container)) as HTMLElement[];

    containers.forEach((container) => {
      const animatedWebp = container.querySelector(SELECTORS.animatedWebp) as HTMLImageElement;
      const staticImg = container.querySelector(SELECTORS.staticImage) as HTMLImageElement;

      if (animatedWebp && staticImg) {
        animatedWebp.style.opacity = '0';
        staticImg.style.opacity = '1';
      }
    });

    currentlyAnimatedElement = null;
  };

  const updateConfig = (newConfig: Partial<AnimatedWebpConfig>): void => {
    Object.assign(defaultConfig, newConfig);
    updateVisibleElement();
  };

  init();

  return {
    destroy,
    updateConfig,
    updateVisibleElement,
  };
};

// inicializace při načtení DOM
export const initAnimatedWebpImages = (): void => {
  createAnimatedWebpImage();
};

export default createAnimatedWebpImage;
